import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageHeaderWithBack, useAlert, useToast, } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { formsService } from "@/api/services/forms.service";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import { formResponseToUpdateDto } from "./utils";
import { buildFormPrintHtml } from "@/utils/formPrintHtml";
import styles from "./Forms.module.css";
const SearchIcon = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35", stroke: "#9CA3AF", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const SortIcon = () => (_jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M6 3L6 9M6 3L3 6M6 3L9 6", stroke: "#1E40AF", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const DownloadIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M14 11V13C14 13.5304 13.7893 14.0391 13.4142 14.4142C13.0391 14.7893 12.5304 15 12 15H3C2.46957 15 1.96086 14.7893 1.58579 14.4142C1.21071 14.0391 1 13.5304 1 13V11M11 5L7.5 1.5M7.5 1.5L4 5M7.5 1.5V11", stroke: "#4B5563", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const RemoveIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "8", cy: "8", r: "7", stroke: "#4B5563", strokeWidth: "1.5" }), _jsx("line", { x1: "5", y1: "8", x2: "11", y2: "8", stroke: "#4B5563", strokeWidth: "1.5", strokeLinecap: "round" })] }));
const MoreIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "8", cy: "3", r: "1", fill: "#4B5563" }), _jsx("circle", { cx: "8", cy: "8", r: "1", fill: "#4B5563" }), _jsx("circle", { cx: "8", cy: "13", r: "1", fill: "#4B5563" })] }));
const EditIcon = () => (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M8.75 2.625L11.375 5.25L4.375 12.25H1.75V9.625L8.75 2.625Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const CheckIcon = () => (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M11.6667 3.5L5.25 9.91667L2.33334 7", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const CancelIcon = () => (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const FileIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z", fill: "#FB923C" }) }));
const formatDate = (dateString) => formatDateOnlyDisplay(dateString);
const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0)
        return "Today";
    if (diffDays === 1)
        return "Yesterday";
    if (diffDays < 7)
        return `${diffDays} days ago`;
    if (diffDays < 30)
        return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? "s" : ""} ago`;
    if (diffDays < 365)
        return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? "s" : ""} ago`;
    return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? "s" : ""} ago`;
};
export const Forms = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showAlert } = useAlert();
    const { showToast } = useToast();
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");
    const [forms, setForms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [editingFormId, setEditingFormId] = useState(null);
    const [editingFormName, setEditingFormName] = useState("");
    const menuRef = useRef(null);
    const editInputRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setActiveMenuId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    const fetchForms = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await formsService.getForms({ pageSize: 100 });
            const formItems = response.data.data.map((form) => ({
                id: form.id,
                name: form.name,
                type: form.templateName || "Building Permit Form",
                lastModified: formatDate(form.updatedAt),
                lastModifiedValue: new Date(form.updatedAt).getTime(),
                created: formatRelativeTime(form.createdAt),
                createdValue: Math.floor((new Date().getTime() - new Date(form.createdAt).getTime()) /
                    (1000 * 60 * 60 * 24)),
            }));
            setForms(formItems);
        }
        catch (err) {
            console.error("Failed to fetch forms:", err);
            setError("Failed to load forms. Please try again.");
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        void fetchForms();
    }, [fetchForms]);
    const filteredForms = useMemo(() => {
        const filtered = forms.filter((form) => form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            form.type.toLowerCase().includes(searchTerm.toLowerCase()));
        const comparator = (a, b) => {
            if (sortField === "name") {
                const comparison = a.name.localeCompare(b.name);
                return sortDirection === "asc" ? comparison : -comparison;
            }
            const valueKey = sortField === "lastModified" ? "lastModifiedValue" : "createdValue";
            const aValue = a[valueKey];
            const bValue = b[valueKey];
            const comparison = aValue - bValue;
            return sortDirection === "asc" ? comparison : -comparison;
        };
        return filtered.sort(comparator);
    }, [forms, searchTerm, sortField, sortDirection]);
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        }
        else {
            setSortField(field);
            setSortDirection("asc");
        }
    };
    const handleViewForm = (formId) => {
        navigate(`/admin/forms/${formId}`);
    };
    const handleEditForm = (formId) => {
        navigate(`/admin/forms/${formId}/edit`);
        setActiveMenuId(null);
    };
    const handleDuplicateForm = async (formId) => {
        try {
            showToast("Duplicating form...", "info");
            await formsService.duplicateForm(formId);
            showToast("Form duplicated successfully", "success");
            fetchForms();
        }
        catch (err) {
            console.error("Failed to duplicate form:", err);
            showToast("Failed to duplicate form", "error");
        }
        finally {
            setActiveMenuId(null);
        }
    };
    const handleDeleteForm = async (formId, formName) => {
        showAlert({
            title: "Delete Form",
            message: `Are you sure you want to delete "${formName}"? This action cannot be undone.`,
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "error",
            onConfirm: async () => {
                try {
                    await formsService.deleteForm(formId);
                    showToast("Form deleted successfully", "success");
                    fetchForms();
                }
                catch (err) {
                    console.error("Failed to delete form:", err);
                    showToast("Failed to delete form", "error");
                }
            },
        });
    };
    const toggleMenu = (formId, event) => {
        event.stopPropagation();
        setActiveMenuId(activeMenuId === formId ? null : formId);
    };
    const handleStartEdit = (formId, currentName, event) => {
        if (event) {
            event.stopPropagation();
        }
        setEditingFormId(formId);
        setEditingFormName(currentName);
        setActiveMenuId(null);
        setTimeout(() => {
            editInputRef.current?.focus();
            editInputRef.current?.select();
        }, 0);
    };
    const handleCancelEdit = () => {
        setEditingFormId(null);
        setEditingFormName("");
    };
    const handleSaveEdit = async (formId) => {
        if (!editingFormName.trim()) {
            showToast("Form name cannot be empty", "error");
            return;
        }
        try {
            const response = await formsService.getForm(formId);
            const fullBody = formResponseToUpdateDto(response.data, {
                name: editingFormName.trim(),
            });
            await formsService.updateForm(formId, fullBody);
            showToast("Form name updated successfully", "success");
            setEditingFormId(null);
            setEditingFormName("");
            fetchForms();
        }
        catch (err) {
            console.error("Failed to update form name:", err);
            showToast("Failed to update form name", "error");
        }
    };
    const handleKeyDown = (event, formId) => {
        if (event.key === "Enter") {
            handleSaveEdit(formId);
        }
        else if (event.key === "Escape") {
            handleCancelEdit();
        }
    };
    const handleDownloadForm = async (formId, formName) => {
        try {
            showToast("Preparing form for download...", "info");
            const response = await formsService.getForm(formId);
            const form = response.data;
            const printWindow = window.open("", "_blank");
            if (!printWindow) {
                showToast("Please allow pop-ups to download forms", "error");
                return;
            }
            const htmlContent = buildFormPrintHtml(form, {
                title: formName,
                autoPrint: true,
            });
            printWindow.document.write(htmlContent);
            printWindow.document.close();
        }
        catch (err) {
            console.error("Failed to download form:", err);
            showToast("Failed to prepare form for download", "error");
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Forms", onBack: () => navigate(-1) }), _jsxs("div", { className: styles.formsRegisterSection, children: [_jsxs("div", { className: styles.registerHeader, children: [_jsx("h2", { className: styles.registerTitle, children: "FORMS REGISTER" }), _jsxs("div", { className: styles.searchContainer, children: [_jsx(SearchIcon, {}), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search...", value: searchTerm, onChange: (event) => setSearchTerm(event.target.value) })] })] }), _jsx("div", { className: styles.tableCard, children: isLoading ? (_jsx("div", { style: { padding: "40px", textAlign: "center" }, children: "Loading forms..." })) : error ? (_jsx("div", { style: { padding: "40px", textAlign: "center", color: "red" }, children: error })) : filteredForms.length === 0 ? (_jsx("div", { style: { padding: "40px", textAlign: "center", color: "#666" }, children: "No forms found. Visit the form builder to create one." })) : (_jsxs("table", { className: styles.table, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsxs("th", { onClick: () => handleSort("name"), className: styles.sortableHeader, children: [_jsx("span", { children: "Form Name" }), _jsx(SortIcon, {})] }), _jsxs("th", { onClick: () => handleSort("lastModified"), className: styles.sortableHeader, children: [_jsx("span", { children: "Last Modified" }), _jsx(SortIcon, {})] }), _jsxs("th", { onClick: () => handleSort("created"), className: styles.sortableHeader, children: [_jsx("span", { children: "Created" }), _jsx(SortIcon, {})] }), _jsx("th", { className: styles.actionsHeader })] }) }), _jsx("tbody", { children: filteredForms.map((form) => (_jsxs("tr", { className: styles.tableRow, children: [_jsx("td", { className: styles.tableCell, children: _jsxs("div", { className: styles.formNameCell, children: [_jsx("div", { className: styles.formIconSmall, children: _jsx(FileIcon, {}) }), _jsxs("div", { className: styles.formNameWrapper, children: [editingFormId === form.id ? (_jsxs("div", { className: styles.editContainer, children: [_jsx("input", { ref: editInputRef, type: "text", className: styles.editInput, value: editingFormName, onChange: (event) => setEditingFormName(event.target.value), onKeyDown: (event) => handleKeyDown(event, form.id), onClick: (event) => event.stopPropagation() }), _jsxs("div", { className: styles.editActions, children: [_jsx("button", { className: styles.editActionBtn, onClick: (event) => {
                                                                                        event.stopPropagation();
                                                                                        handleSaveEdit(form.id);
                                                                                    }, title: "Save", children: _jsx(CheckIcon, {}) }), _jsx("button", { className: styles.editActionBtn, onClick: (event) => {
                                                                                        event.stopPropagation();
                                                                                        handleCancelEdit();
                                                                                    }, title: "Cancel", children: _jsx(CancelIcon, {}) })] })] })) : (_jsxs("div", { className: styles.formNameDisplay, children: [_jsx("div", { className: styles.formName, children: form.name }), _jsx("button", { className: styles.editNameBtn, onClick: (event) => handleStartEdit(form.id, form.name, event), title: "Edit form name", children: _jsx(EditIcon, {}) })] })), _jsx("div", { className: styles.formType, children: form.type })] })] }) }), _jsx("td", { className: styles.tableCell, children: form.lastModified }), _jsx("td", { className: styles.tableCell, children: form.created }), _jsxs("td", { className: styles.tableCellActions, children: [_jsx("button", { className: styles.viewFormBtn, onClick: () => handleViewForm(form.id), children: "VIEW FORM" }), _jsx("button", { className: styles.iconBtn, title: "Download", onClick: () => handleDownloadForm(form.id, form.name), children: _jsx(DownloadIcon, {}) }), _jsx("button", { className: styles.iconBtn, title: "Remove", onClick: () => handleDeleteForm(form.id, form.name), children: _jsx(RemoveIcon, {}) }), _jsxs("div", { className: styles.actionsWrapper, ref: activeMenuId === form.id ? menuRef : null, children: [_jsx("button", { className: styles.iconBtn, title: "More options", onClick: (event) => toggleMenu(form.id, event), children: _jsx(MoreIcon, {}) }), activeMenuId === form.id && (_jsxs("div", { className: styles.dropdownMenu, children: [_jsx("button", { className: styles.dropdownItem, onClick: () => handleEditForm(form.id), children: "Edit" }), _jsx("button", { className: styles.dropdownItem, onClick: () => handleDuplicateForm(form.id), children: "Copy" })] }))] })] })] }, form.id))) })] })) })] })] }));
};
